import type { GameCommand, MemberView, VisualAsset } from "../domain/types";
import type { ExperienceRating, RatingSummary, RatingTagId } from "../domain/ratings";

export interface CatalogItem {
  id: string;
  slug: string;
  version: string;
  title: string;
  tagline: string;
  description: string;
  format: "mystery" | "connection" | "party";
  minPlayers: number;
  maxPlayers: number;
  recommendedDevices: "one" | "multiple" | "either";
  estimatedMinutes: number;
  priceMxn: number;
  difficulty: string;
  intensity: string;
  roles: Array<{ id: string; label: string }>;
  contentRating: { age: number; warnings: string[] };
  cover: { motif: string; accent: "citron" | "coral" | "sky"; alt: string; image?: VisualAsset };
  ratingSummary: RatingSummary;
}

export interface RoomAccess {
  roomId: string;
  inviteCode: string;
  memberToken: string;
  memberId: string;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...init?.headers
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(
      response.status,
      payload.error?.code ?? "REQUEST_FAILED",
      payload.error?.message ?? "No pudimos completar la solicitud.",
      payload.error?.details
    );
  }
  return payload as T;
}

export const api = {
  async catalog(): Promise<CatalogItem[]> {
    return (await request<{ items: CatalogItem[] }>("/api/catalog")).items;
  },

  async product(slug: string): Promise<CatalogItem> {
    return (await request<{ item: CatalogItem }>(`/api/catalog/${encodeURIComponent(slug)}`)).item;
  },

  async createOrder(slug: string, buyerEmail?: string) {
    return request<{
      order: {
        orderId: string;
        entitlementId: string;
        status: "paid";
        amount: number;
        currency: "MXN";
        playToken?: string;
        idempotentReplay: boolean;
      };
      paymentMode: "demo" | "live";
    }>("/api/orders", {
      method: "POST",
      headers: { "idempotency-key": crypto.randomUUID() },
      body: JSON.stringify({ slug, buyerEmail })
    });
  },

  async order(orderId: string) {
    return request<{ order: Record<string, string | number> }>(`/api/orders/${encodeURIComponent(orderId)}`);
  },

  async createRoom(playToken: string, hostName: string, deviceMode: "shared" | "multiple") {
    return request<{ access: RoomAccess }>("/api/rooms", {
      method: "POST",
      body: JSON.stringify({ playToken, hostName, deviceMode })
    });
  },

  async joinRoom(code: string, displayName: string) {
    return request<{ access: RoomAccess }>("/api/rooms/join", {
      method: "POST",
      body: JSON.stringify({ code, displayName })
    });
  },

  async switchDeviceMode(roomId: string, memberToken: string, deviceMode: "shared" | "multiple") {
    return request<{ view: MemberView }>(`/api/rooms/${encodeURIComponent(roomId)}/mode`, {
      method: "PATCH",
      headers: { authorization: `Bearer ${memberToken}` },
      body: JSON.stringify({ deviceMode })
    });
  },

  async snapshot(roomId: string, memberToken: string): Promise<MemberView> {
    return (await request<{ view: MemberView }>(`/api/rooms/${encodeURIComponent(roomId)}/snapshot`, {
      headers: { authorization: `Bearer ${memberToken}` }
    })).view;
  },

  async poll(roomId: string, memberToken: string, sinceRevision: number): Promise<{ changed: boolean; revision: number; view?: MemberView }> {
    return request<{ changed: boolean; revision: number; view?: MemberView }>(
      `/api/rooms/${encodeURIComponent(roomId)}/poll?since=${sinceRevision}`,
      { headers: { authorization: `Bearer ${memberToken}` } }
    );
  },

  async rating(roomId: string, memberToken: string): Promise<ExperienceRating | null> {
    return (await request<{ rating: ExperienceRating | null }>(`/api/rooms/${encodeURIComponent(roomId)}/rating`, {
      headers: { authorization: `Bearer ${memberToken}` }
    })).rating;
  },

  async saveRating(roomId: string, memberToken: string, value: number, tags: RatingTagId[]) {
    return request<{ rating: ExperienceRating; summary: RatingSummary }>(`/api/rooms/${encodeURIComponent(roomId)}/rating`, {
      method: "PUT",
      headers: { authorization: `Bearer ${memberToken}` },
      body: JSON.stringify({ value, tags })
    });
  },

  async command(roomId: string, memberToken: string, command: GameCommand) {
    return request<{ view: MemberView; events: string[]; replayed: boolean }>(
      `/api/rooms/${encodeURIComponent(roomId)}/commands`,
      {
        method: "POST",
        headers: { authorization: `Bearer ${memberToken}` },
        body: JSON.stringify(command)
      }
    );
  },

  async track(eventName: string, subjectRef: string, properties: Record<string, unknown> = {}) {
    try {
      await request("/api/events", {
        method: "POST",
        body: JSON.stringify({ eventName, subjectRef, properties })
      });
    } catch {
      // La analítica nunca bloquea compra o juego.
    }
  },

  async admin<T>(path: string, adminKey: string, init?: RequestInit): Promise<T> {
    return request<T>(`/api/admin${path}`, {
      ...init,
      headers: { "x-admin-key": adminKey, "x-admin-actor": "local-editor", ...init?.headers }
    });
  }
};

export function saveRoomAccess(access: RoomAccess): void {
  sessionStorage.setItem(`room:${access.roomId}`, JSON.stringify(access));
}

export function loadRoomAccess(roomId: string): RoomAccess | undefined {
  const stored = sessionStorage.getItem(`room:${roomId}`);
  if (!stored) return undefined;
  try {
    return JSON.parse(stored) as RoomAccess;
  } catch {
    return undefined;
  }
}
